import { calculatePrices, getDirectColumnPrice, getSupplierCostPrice, getMpcOverridePrice } from '../../shared/pricing';
import { mapCategories as mapPricingCategories } from '../../shared/categoryMapping';
/**
 * Centralizirana Sync Logika - Supabase → WooCommerce
 *
 * JEDNA FUNKCIJA koju koriste:
 * - Live Preview (simulacija + stvarni sync)
 * - Cron job
 * - Manual sync
 *
 * Čita konfiguraciju iz woo_shops tabele (Supabase).
 */

import { WooShop, Product as SupabaseProduct } from '../lib/supabase';

// ============================================================================
// TYPES
// ============================================================================

export interface SyncConfig {
  shop: WooShop;
  sourceProduct: SupabaseProduct;
  targetProduct?: WooTargetProduct | null;  // Ako postoji u target shopu
}

export interface WooTargetProduct {
  id: number;
  sku: string;
  name: string;
  status: string;
  regular_price: string;
  sale_price?: string;
  stock_quantity?: number;
  stock_status?: string;
  categories?: { id: number; name: string }[];
  images?: { id: number; src: string }[];
  attributes?: any[];
  meta_data?: { key: string; value: any }[];
}

export interface WooProductData {
  sku: string;
  name: string;
  status?: string;
  type?: string;
  regular_price?: string;
  sale_price?: string;
  description?: string;
  short_description?: string;
  manage_stock?: boolean;
  stock_quantity?: number;
  stock_status?: string;
  weight?: string;
  dimensions?: { length?: string; width?: string; height?: string };
  categories?: { id?: number; name?: string }[];
  images?: { src: string; name?: string; alt?: string }[];
  meta_data?: { key: string; value: any }[];
  attributes?: any[];
}

export interface SyncResult {
  action: 'create' | 'update' | 'skip';
  success: boolean;
  productId?: number;
  sku: string;
  data: WooProductData;
  message: string;
  error?: string;
  priceCalculation?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Konsolidirana količina iz supplier_stocks
 */
function getConsolidatedStock(product: SupabaseProduct): number {
  if (product.supplier_stocks && typeof product.supplier_stocks === 'object') {
    return Object.values(product.supplier_stocks).reduce((sum: number, qty: any) => {
      const n = typeof qty === 'number' ? qty : parseInt(qty) || 0;
      return sum + n;
    }, 0);
  }
  return product.stock_quantity || 0;
}

const normalizeStockKey = (v: any): string => String(v ?? '').replace(/^"|"$/g, '').trim().toUpperCase();

/**
 * Smije li lager ovog dobavljača ući u efektivni stock proizvoda na shopu.
 * Mora ostati identično isSupplierStockAllowed u api/lib/syncProductLogic.ts —
 * raskorak između ručnog (ova kopija) i serverskog sync-a = vječna update petlja.
 */
function isSupplierStockAllowed(product: SupabaseProduct, supplier: string, filter: any): boolean {
  if (!filter) return true;

  const cats: string[] = Array.isArray(filter.categories) ? filter.categories : [];
  const catSet = new Set(cats.map(normalizeStockKey).filter(Boolean));
  // Isključene kategorije (blacklist) — exclude uvijek pobjeđuje allow listu.
  const excluded: string[] = Array.isArray(filter.excluded_categories) ? filter.excluded_categories : [];
  const excludedSet = new Set(excluded.map(normalizeStockKey).filter(Boolean));
  const brandMap = filter.category_brands && typeof filter.category_brands === 'object'
    ? filter.category_brands
    : null;
  const brand = normalizeStockKey((product as any).brand);

  const brandAllowedIn = (list: any): boolean =>
    Array.isArray(list) && list.length > 0
      ? brand.length > 0 && list.map(normalizeStockKey).includes(brand)
      : true;

  const checkKnownCategory = (kat: string): boolean => {
    if (excludedSet.has(kat)) return false;
    if (catSet.size > 0 && !catSet.has(kat)) return false;
    if (!brandMap) return true;
    const entry = Object.entries(brandMap).find(([k]) => normalizeStockKey(k) === kat);
    return brandAllowedIn(entry?.[1]);
  };

  // Poznata kategorija OVOG dobavljača za ovaj proizvod — autoritativna (Faza 2)
  const supCats = (product as any).supplier_categories;
  if (supCats && typeof supCats === 'object' && !Array.isArray(supCats)) {
    const want = normalizeStockKey(supplier);
    for (const [k, v] of Object.entries(supCats)) {
      if (normalizeStockKey(k) === want) {
        const kat = normalizeStockKey(v);
        if (kat) return checkKnownCategory(kat);
        break;
      }
    }
  }

  const isPrimary = normalizeStockKey(product.dobavljac) === normalizeStockKey(supplier);
  if (isPrimary && product.categories !== undefined && product.categories !== null) {
    return checkKnownCategory(normalizeStockKey(product.categories));
  }

  if (!brandMap) return true;

  const restricted = new Set(
    Object.entries(brandMap)
      .filter(([, list]) => Array.isArray(list) && list.length > 0)
      .map(([k]) => normalizeStockKey(k))
  );
  if (restricted.size === 0) return true;
  if (catSet.size === 0 || [...catSet].some(c => !restricted.has(c))) return true;

  const svi = Object.entries(brandMap)
    .filter(([k]) => restricted.has(normalizeStockKey(k)))
    .flatMap(([, list]) => (Array.isArray(list) ? list : []));
  return brandAllowedIn(svi);
}

/**
 * Efektivni stock za shop: zbir supplier_stocks SAMO za dobavljače dozvoljene na shopu.
 * Isto ponašanje kao getEffectiveStock u api/lib/syncProductLogic.ts.
 */
function getEffectiveStock(
  product: SupabaseProduct,
  allowedSuppliers?: string[],
  supplierFilters?: any[]
): number {
  const stocks = product.supplier_stocks;
  if (
    !allowedSuppliers?.length ||
    !stocks || typeof stocks !== 'object' || Array.isArray(stocks) ||
    Object.keys(stocks).length === 0
  ) {
    return getConsolidatedStock(product);
  }

  const allowed = new Set(allowedSuppliers.map(s => String(s).trim().toUpperCase()));
  const filterMap = new Map<string, any>();
  for (const f of Array.isArray(supplierFilters) ? supplierFilters : []) {
    if (f?.enabled === false || !f?.supplier) continue;
    filterMap.set(normalizeStockKey(f.supplier), f);
  }

  let sum = 0;
  for (const [supplier, qty] of Object.entries(stocks)) {
    if (!allowed.has(supplier.trim().toUpperCase())) continue;
    if (filterMap.size > 0 && !isSupplierStockAllowed(product, supplier, filterMap.get(normalizeStockKey(supplier)))) {
      continue;
    }
    const n = typeof qty === 'number' ? qty : parseInt(String(qty), 10);
    if (!isNaN(n) && n > 0) sum += n;
  }
  return sum;
}

/** MPC dobavljači iz pricing_config (mpcOverride.suppliers ili legacy doperMpcOverride) */
function getMpcSupplierNames(pricingConfig: any): Set<string> {
  const cfg = pricingConfig?.mpcOverride;
  if (cfg !== null && typeof cfg === 'object') {
    if (cfg.enabled !== true) return new Set();
    return new Set(
      (Array.isArray(cfg.suppliers) ? cfg.suppliers : [])
        .map((s: any) => normalizeStockKey(typeof s === 'string' ? s : s?.name))
        .filter(Boolean)
    );
  }
  return pricingConfig?.doperMpcOverride?.enabled === true ? new Set(['DOPER']) : new Set();
}

/**
 * Efektivna nabavna (Faza 3) — mora ostati identično getEffectiveNabavna u
 * api/lib/syncProductLogic.ts (raskorak ručnog i serverskog synca = petlja).
 * Primarni dobavljač blokiran filterom shopa ne smije biti izvor nabavne:
 * uzima se najjeftiniji dozvoljeni sa lagerom, MPC dobavljači se preskaču.
 */
function getEffectiveNabavna(product: SupabaseProduct, sourceFilters: any, pricingConfig: any): number {
  const original = parseFloat(String(product.nabavna_cijena || 0)) || 0;

  const suppliers: string[] | undefined = sourceFilters?.suppliers;
  const supplierFilters = Array.isArray(sourceFilters?.supplier_filters) ? sourceFilters.supplier_filters : [];
  if (!suppliers?.length || supplierFilters.length === 0) return original;

  const allowed = new Set(suppliers.map(s => normalizeStockKey(s)));
  const filterMap = new Map<string, any>();
  for (const f of supplierFilters) {
    if (f?.enabled === false || !f?.supplier) continue;
    filterMap.set(normalizeStockKey(f.supplier), f);
  }
  if (filterMap.size === 0) return original;

  const primary = normalizeStockKey(product.dobavljac);
  if (allowed.has(primary) && isSupplierStockAllowed(product, String(product.dobavljac ?? ''), filterMap.get(primary))) {
    return original;
  }

  const prices = product.supplier_prices;
  const stocks = product.supplier_stocks;
  if (!prices || typeof prices !== 'object' || Array.isArray(prices)) return original;

  const mpcSuppliers = getMpcSupplierNames(pricingConfig);
  const stockOf = (sup: string): number => {
    if (!stocks || typeof stocks !== 'object' || Array.isArray(stocks)) return 0;
    for (const [k, v] of Object.entries(stocks)) {
      if (normalizeStockKey(k) === sup) {
        const n = typeof v === 'number' ? v : parseInt(String(v), 10);
        return isNaN(n) ? 0 : n;
      }
    }
    return 0;
  };

  let best = 0;
  for (const [sup, value] of Object.entries(prices)) {
    const key = normalizeStockKey(sup);
    if (key === primary || !allowed.has(key) || mpcSuppliers.has(key)) continue;
    if (!isSupplierStockAllowed(product, sup, filterMap.get(key))) continue;
    if (stockOf(key) <= 0) continue;
    const p = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    if (p > 0 && (best === 0 || p < best)) best = p;
  }

  return best > 0 ? best : original;
}

/** Kopija proizvoda sa efektivnom nabavnom — zove se na ulazu buildProductData */
function applyEffectiveNabavna(product: SupabaseProduct, sourceFilters: any, pricingConfig: any): SupabaseProduct {
  const effective = getEffectiveNabavna(product, sourceFilters, pricingConfig);
  const original = parseFloat(String(product.nabavna_cijena || 0)) || 0;
  if (Math.abs(effective - original) < 0.005) return product;
  return { ...product, nabavna_cijena: effective };
}

/**
 * Efektivna kategorija za mapiranje (Faza 3b) — mora ostati identično
 * getEffectiveCategories u api/lib/syncProductLogic.ts. Primarni nije dozvoljen
 * na shopu → kategorija prvog dozvoljenog dobavljača sa lagerom iz
 * supplier_categories (inače mapiranje pada u default kategoriju).
 */
function getEffectiveCategories(product: SupabaseProduct, sourceFilters: any): any {
  const original = (product as any).categories;

  const suppliers: string[] | undefined = sourceFilters?.suppliers;
  if (!suppliers?.length) return original;

  const supplierFilters = Array.isArray(sourceFilters?.supplier_filters) ? sourceFilters.supplier_filters : [];
  const filterMap = new Map<string, any>();
  for (const f of supplierFilters) {
    if (f?.enabled === false || !f?.supplier) continue;
    filterMap.set(normalizeStockKey(f.supplier), f);
  }

  const allowed = new Set(suppliers.map(s => normalizeStockKey(s)));
  const primary = normalizeStockKey(product.dobavljac);
  if (allowed.has(primary) && isSupplierStockAllowed(product, String(product.dobavljac ?? ''), filterMap.get(primary))) {
    return original;
  }

  const supCats = (product as any).supplier_categories;
  const stocks = product.supplier_stocks;
  if (!supCats || typeof supCats !== 'object' || Array.isArray(supCats)) return original;

  const entryOf = (map: any, sup: string): any => {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return undefined;
    for (const [k, v] of Object.entries(map)) {
      if (normalizeStockKey(k) === sup) return v;
    }
    return undefined;
  };

  for (const supplier of suppliers) {
    const key = normalizeStockKey(supplier);
    if (key === primary) continue;
    const rawStock = entryOf(stocks, key);
    const sStock = typeof rawStock === 'number' ? rawStock : parseInt(String(rawStock ?? 0), 10) || 0;
    if (sStock <= 0) continue;
    if (!isSupplierStockAllowed(product, supplier, filterMap.get(key))) continue;
    const kat = entryOf(supCats, key);
    if (kat !== undefined && kat !== null && String(kat).trim() !== '') return kat;
  }

  return original;
}

/**
 * Izuzeci sinhronizacije po kategoriji — kopija getCategoryExcludedFields iz
 * api/lib/syncProductLogic.ts (držati u sinhronu!). Polja izuzeta za efektivnu
 * kategoriju proizvoda se NE ažuriraju na postojećim proizvodima.
 */
function getCategoryExcludedFields(product: SupabaseProduct, sourceFilters: any): Set<string> {
  const excluded = new Set<string>();
  const rules = sourceFilters?.category_field_exclusions;
  if (!Array.isArray(rules) || rules.length === 0) return excluded;

  const rawCat = getEffectiveCategories(product, sourceFilters);
  const productCats = new Set(
    (Array.isArray(rawCat) ? rawCat : [rawCat]).map(normalizeStockKey).filter(Boolean)
  );
  if (productCats.size === 0) return excluded;

  for (const rule of rules) {
    if (!rule || rule.enabled === false) continue;
    const cats = Array.isArray(rule.categories) ? rule.categories : [];
    if (!cats.some((c: any) => productCats.has(normalizeStockKey(c)))) continue;
    for (const f of Array.isArray(rule.fields) ? rule.fields : []) {
      if (typeof f === 'string' && f.trim()) excluded.add(f.trim());
    }
  }

  if (excluded.has('regular_price')) excluded.add('sale_price');
  if (excluded.has('stock_quantity')) {
    excluded.add('stock_status');
    excluded.add('manage_stock');
  }
  if (excluded.has('description')) excluded.add('short_description');

  return excluded;
}

/**
 * Formatira supplier_stocks u čitljiv string sa cijenama
 * Input:  stocks={"COMTRADE": 2, "ASBIS": 5}, prices={"COMTRADE": 150, "ASBIS": 145}
 * Output: "ASBIS LAGER: 5kom, NC: 145 KM; COMTRADE LAGER: 2kom, NC: 150 KM"
 *
 * Sortira po količini (najviše prvo), pa po cijeni (najniža prvo)
 */
function formatSupplierStocks(
  supplierStocks: Record<string, number> | undefined,
  supplierPrices?: Record<string, number>
): string {
  if (!supplierStocks || typeof supplierStocks !== 'object') {
    return '';
  }

  // Kreiraj array sa svim podacima za sortiranje
  const entries: { supplier: string; qty: number; price: number }[] = [];

  for (const [supplier, qty] of Object.entries(supplierStocks)) {
    const quantity = typeof qty === 'number' ? qty : parseInt(String(qty)) || 0;
    const price = supplierPrices?.[supplier] ?? 0;
    entries.push({ supplier, qty: quantity, price: typeof price === 'number' ? price : parseFloat(String(price)) || 0 });
  }

  // Sortiraj: prvo po količini DESC, pa po cijeni ASC
  entries.sort((a, b) => {
    if (b.qty !== a.qty) return b.qty - a.qty; // Više zaliha prvo
    return a.price - b.price; // Jeftiniji prvo
  });

  // Formatiraj output
  const parts: string[] = [];
  for (const { supplier, qty, price } of entries) {
    const priceStr = price > 0 ? price.toFixed(2) : '0';
    parts.push(`${supplier} LAGER: ${qty}kom, NC: ${priceStr} KM`);
  }

  return parts.join('; ');
}

/**
 * Primijeni image proxy na URL slike
 *
 * @param imageUrl - Originalni URL slike
 * @param proxyTemplate - Proxy URL template sa placeholder-om {url}
 *                        npr. "https://proxy.com/img?url={url}"
 * @returns Proxy URL ili originalni URL ako proxy nije definiran
 */
function applyImageProxy(imageUrl: string, proxyTemplate?: string): string {
  if (!proxyTemplate || !imageUrl) {
    return imageUrl;
  }

  // Zamijeni {url} sa enkodiranim originalnim URL-om
  return proxyTemplate.replace('{url}', encodeURIComponent(imageUrl));
}

/**
 * Pronađi image proxy URL za dobavljača
 */
function getSupplierImageProxy(
  supplierName: string | undefined,
  supplierFilters: any[] | undefined
): string | undefined {
  if (!supplierName || !supplierFilters?.length) {
    return undefined;
  }

  const supplierFilter = supplierFilters.find(
    (sf: any) => sf.supplier?.toLowerCase() === supplierName.toLowerCase() && sf.enabled
  );

  return supplierFilter?.image_proxy_url;
}


/**
 * Primijeni name rules na naziv proizvoda (per-supplier)
 */
function applyNameRules(
  name: string,
  nameRulesConfig: any,
  supplierName?: string
): string {
  if (!nameRulesConfig?.enabled || !nameRulesConfig?.rules?.length) {
    return name;
  }

  // Nađi pravilo za ovog dobavljača
  const rule = nameRulesConfig.rules.find((r: any) =>
    r.enabled && r.supplierName === supplierName
  );

  if (!rule) {
    return name;
  }

  let result = name;
  const transformation = rule.transformation || {};

  // 1. Strip HTML
  if (transformation.stripHtml) {
    result = result.replace(/<[^>]*>/g, '');
  }

  // 2. Ukloni specifične znakove
  if (transformation.removeChars?.length) {
    for (const char of transformation.removeChars) {
      result = result.split(char).join('');
    }
  }

  // 3. Max words - ograniči broj riječi
  if (transformation.maxWords && transformation.maxWords > 0) {
    const words = result.trim().split(/\s+/);
    if (words.length > transformation.maxWords) {
      result = words.slice(0, transformation.maxWords).join(' ');
    }
  }

  // 4. Text case transformation
  if (transformation.textCase) {
    switch (transformation.textCase) {
      case 'uppercase':
        result = result.toUpperCase();
        break;
      case 'lowercase':
        result = result.toLowerCase();
        break;
      case 'capitalize':
        result = result.split(' ').map((word: string) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
        break;
      // 'none' - bez promjene
    }
  }

  // 5. Prefix
  if (rule.prefix) {
    result = rule.prefix + result;
  }

  // 6. Suffix
  if (rule.suffix) {
    result = result + rule.suffix;
  }

  // 7. Trim i normaliziraj razmake
  if (transformation.trim !== false) {
    result = result.trim().replace(/\s+/g, ' ');
  }

  return result;
}

/**
 * Mapiraj kategorije iz source na target
 */
function mapCategories(
  sourceCategories: string | any[] | undefined,
  categoryMappings: any[]
): { id?: number; name: string; _mapped: boolean }[] {
  if (!sourceCategories) return [];

  // Parsiraj source kategorije
  let categories: string[] = [];
  if (typeof sourceCategories === 'number') {
    categories = [String(sourceCategories)];
  } else if (typeof sourceCategories === 'string' && sourceCategories) {
    categories = sourceCategories
      .replace(/[()]/g, '')
      .split(/[|>]/)
      .map(s => s.trim())
      .filter(Boolean);
  } else if (Array.isArray(sourceCategories)) {
    categories = sourceCategories
      .map(c => typeof c === 'string' ? c : (typeof c === 'number' ? String(c) : c?.name))
      .filter(Boolean);
  }

  // Mapiraj
  return categories.map(cat => {
    const mapping = categoryMappings?.find((m: any) =>
      (m.sourceValue || m.sourceFieldValue) === cat ||
      (m.sourceValue || m.sourceFieldValue)?.toLowerCase() === cat.toLowerCase()
    );
    if (mapping?.targetId) {
      return { id: mapping.targetId, name: mapping.targetName || cat, _mapped: true };
    }
    return { name: cat, _mapped: false };
  });
}

/**
 * Mapiraj atribute iz specifikacija
 */
function mapAttributes(
  specifikacije: Record<string, any> | undefined,
  attributeMappings: any[]
): any[] {
  if (!specifikacije || typeof specifikacije !== 'object') return [];
  // Ako nema mapiranja, NE vraćaj specifikacije (izbjegava spam atributa)
  if (!attributeMappings || attributeMappings.length === 0) return [];

  const attributes: any[] = [];

  for (const [key, value] of Object.entries(specifikacije)) {
    if (!value) continue;

    const mapping = attributeMappings?.find((m: any) =>
      m.sourceAttribute === key ||
      m.sourceAttribute?.toLowerCase() === key.toLowerCase()
    );

    // SAMO ako ima eksplicitno mapiranje - ne dodaj nemapirane specifikacije
    if (!mapping?.targetAttribute) continue;

    const attrValue = String(value).substring(0, 200); // Limit length

    attributes.push({
      name: mapping.targetAttribute,
      options: [attrValue],
      visible: true,
      variation: false,
      _source: key,
      _mapped: true
    });
  }

  return attributes;
}

// ============================================================================
// MAIN BUILD FUNCTION
// ============================================================================

/**
 * CENTRALNA FUNKCIJA - Gradi WooCommerce product data
 *
 * Koristi se za:
 * - Preview (simulacija)
 * - Stvarni sync (create/update)
 */
export function buildProductData(config: SyncConfig): {
  data: WooProductData;
  action: 'create' | 'update';
  priceCalculation: string;
} {
  const { shop, targetProduct } = config;
  const isExisting = !!targetProduct;

  const pricingConfig = shop.pricing_config || {};
  const sourceFilters: any = shop.source_filters || {};
  const nameRulesConfig = (shop as any).name_rules_config;

  // Efektivna nabavna (Faza 3) — identično serverskom buildProductData
  const sourceProduct = applyEffectiveNabavna(config.sourceProduct, sourceFilters, pricingConfig);

  // Izuzeci po kategoriji — identično serverskom buildProductData: za postojeće
  // proizvode izuzeta polja se ne šalju (gating ispod je "!isExisting || X === true")
  const categoryExcluded = getCategoryExcludedFields(sourceProduct, sourceFilters);
  const syncFields: Record<string, boolean> = { ...(shop.sync_fields || {}) };
  for (const f of categoryExcluded) syncFields[f] = false;

  // Sync options
  const syncProfile = shop.syncProfile || {};
  const options = syncProfile.options || {};
  const newProductStatus = options.newProductStatus || 'draft';

  // Pricing applyTo settings
  // Default: primijeni pricing na nove proizvode (true), ne primjenjuj na postojeće (false)
  const applyTo = pricingConfig.applyTo || {};
  const hasUpdatePriceNo = isExisting && applyTo.skipUpdatePriceNo && targetProduct?.meta_data?.some((m: any) => m.key === '_updateprice' && String(m.value).toUpperCase() === 'NE');
  const shouldApplyPricing = hasUpdatePriceNo ? false : isExisting
    ? applyTo.existingProducts === true
    : applyTo.newProducts !== false;  // undefined ili true = primijeni

  // Cost price
  const costPrice = parseFloat(
    String(sourceProduct.nabavna_cijena || sourceProduct.regular_price || 0)
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILD PRODUCT DATA
  // ═══════════════════════════════════════════════════════════════════════════

  const data: WooProductData = {
    sku: sourceProduct.sku,
  };

  // NAME - poštuj sync_fields.name za postojeće proizvode
  // Za nove uvijek postavi name, za postojeće samo ako je sync_fields.name true
  if (!isExisting || syncFields.name === true) {
    data.name = applyNameRules(
      sourceProduct.name || sourceProduct.sku,
      nameRulesConfig,
      sourceProduct.dobavljac
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STATUS
  // ─────────────────────────────────────────────────────────────────────────────
  if (!isExisting) {
    // Novi proizvod - koristi newProductStatus
    data.status = newProductStatus;
  }
  // Za postojeće NE šaljemo status - zadržava se postojeći

  // ─────────────────────────────────────────────────────────────────────────────
  // PRICES - poštuj sync_fields za postojeće proizvode
  // ─────────────────────────────────────────────────────────────────────────────
  let priceCalculation = '';
  const shouldSyncPrices = !isExisting || syncFields.regular_price === true;

  const defaultCategory = sourceFilters.default_category_id ? { id: sourceFilters.default_category_id, name: sourceFilters.default_category_name || 'Nekategorizirano' } : undefined;
  const targetCategoryIds = mapPricingCategories(getEffectiveCategories(sourceProduct, sourceFilters), sourceFilters.category_custom_field_mappings || [], defaultCategory).map(c => c.id).filter((id): id is number => id !== undefined);
  const mpcValue = getMpcOverridePrice(sourceProduct, pricingConfig);
  if (mpcValue > 0 && (!isExisting || !categoryExcluded.has('regular_price'))) {
    data.regular_price = String(mpcValue);
    priceCalculation = 'MPC dobavljača: ' + mpcValue.toFixed(2) + ' KM (gotova cijena)';
  } else if (shouldSyncPrices) {
    // Direct column mode - čitaj cijenu direktno iz Supabase kolone
    const directColumnResult = getDirectColumnPrice(sourceProduct, pricingConfig);
    if (directColumnResult) {
      data.regular_price = directColumnResult.regular_price;
      if (directColumnResult.sale_price && (!isExisting || syncFields.sale_price === true)) {
        data.sale_price = directColumnResult.sale_price;
      }
      priceCalculation = 'Direktna cijena: ' + directColumnResult.regular_price + ' KM';
    } else if (shouldApplyPricing && costPrice > 0) {
      // Check if supplier has costPriceColumn override
      const supplierCostOverride = getSupplierCostPrice(sourceProduct, pricingConfig);
      const effectiveCostPrice = supplierCostOverride ?? costPrice;
      const prices = calculatePrices(effectiveCostPrice, pricingConfig, true, sourceProduct.dobavljac, targetCategoryIds);
      if (prices.regular_price) {
        data.regular_price = prices.regular_price;
      }
      if (prices.sale_price && (!isExisting || syncFields.sale_price === true)) {
        data.sale_price = prices.sale_price;
      }
      priceCalculation = prices.regular_price ? `Nabavna ${effectiveCostPrice.toFixed(2)} KM → redovna ${prices.regular_price} KM${prices.sale_price ? ', akcijska ' + prices.sale_price + ' KM' : ''} (pravila shopa)` : 'Pricing nije primijenjen';
      if (supplierCostOverride !== null) {
        priceCalculation += ` [costPriceColumn override: ${effectiveCostPrice} umjesto nabavne ${costPrice}]`;
      }
    } else if (!isExisting) {
      // Novi + pricing isključen = koristi source cijene
      if (sourceProduct.regular_price) {
        data.regular_price = String(sourceProduct.regular_price);
      }
      if (sourceProduct.sale_price) {
        data.sale_price = String(sourceProduct.sale_price);
      }
      priceCalculation = 'Source cijena (pricing isključen za nove)';
    }
  } else {
    // Postojeći + sync_fields.regular_price=false = NE ŠALJEMO cijene (zadržavaju se postojeće)
    priceCalculation = 'Zadržana postojeća cijena (sync_fields.regular_price=false)';
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STOCK - poštuj sync_fields za postojeće proizvode
  // ─────────────────────────────────────────────────────────────────────────────
  if (data.regular_price && !data.sale_price && isExisting && !categoryExcluded.has('sale_price')) data.sale_price = '';

  const consolidatedStock = getEffectiveStock(sourceProduct, (sourceFilters as any).suppliers, (sourceFilters as any).supplier_filters);
  const shouldSyncStock = !isExisting || syncFields.stock_quantity === true;

  if (shouldSyncStock) {
    if (!isExisting || syncFields.manage_stock === true) {
      data.manage_stock = true;
    }
    data.stock_quantity = consolidatedStock;
    if (!isExisting || syncFields.stock_status === true) {
      data.stock_status = consolidatedStock > 0 ? 'instock' : 'outofstock';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // DESCRIPTIONS - poštuj sync_fields za postojeće proizvode
  // Za nove uvijek, za postojeće samo ako je sync_fields.description true
  // ─────────────────────────────────────────────────────────────────────────────
  const shouldSyncDescription = !isExisting || syncFields.description === true;

  if (shouldSyncDescription) {
    if (sourceProduct.description) {
      data.description = sourceProduct.description;
    }
    if (sourceProduct.short_description) {
      data.short_description = sourceProduct.short_description;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // CATEGORIES - poštuj sync_fields za postojeće proizvode
  // Za nove uvijek, za postojeće samo ako je sync_fields.categories true
  // ─────────────────────────────────────────────────────────────────────────────
  const shouldSyncCategories = !isExisting || syncFields.categories === true;

  if (shouldSyncCategories) {
    const categoryMappings = sourceFilters.category_custom_field_mappings || [];
    const mappedCategories = mapCategories(getEffectiveCategories(sourceProduct, sourceFilters), categoryMappings);

    // Filtriraj samo mapirane kategorije (sa ID-jem)
    const validCategories = mappedCategories.filter(c => c.id);
    if (validCategories.length > 0) {
      data.categories = validCategories.map(c => ({ id: c.id, name: c.name }));
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // IMAGES - poštuj sync_fields
  // Za nove uvijek dodaj, za postojeće samo ako je sync_fields.images true
  // (updateProduct() će dodatno provjeriti da li target već ima slike)
  // Koristi image proxy ako je konfiguriran za dobavljača
  // ─────────────────────────────────────────────────────────────────────────────
  const shouldSyncImages = !isExisting || syncFields.images === true;

  if (shouldSyncImages && sourceProduct.images) {
    let images = sourceProduct.images;
    if (typeof images === 'string') {
      images = images.split('|').filter(Boolean).map(src => ({ src }));
    }
    if (Array.isArray(images) && images.length > 0) {
      // Provjeri da li dobavljač ima konfiguriran image proxy
      const imageProxyUrl = getSupplierImageProxy(
        sourceProduct.dobavljac,
        sourceFilters.supplier_filters
      );

      data.images = images
        .filter((img: any) => img && (img.src || typeof img === 'string'))
        .slice(0, 10)
        .map((img: any) => {
          const originalSrc = typeof img === 'string' ? img : img.src;
          // Primijeni proxy ako postoji
          const finalSrc = applyImageProxy(originalSrc, imageProxyUrl);

          return {
            src: finalSrc,
            name: img.name || '',
            alt: img.alt || sourceProduct.name || '',
          };
        });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // ATTRIBUTES - poštuj sync_fields za postojeće proizvode
  // Za nove uvijek, za postojeće samo ako je sync_fields.attributes true
  // ─────────────────────────────────────────────────────────────────────────────
  const shouldSyncAttributes = !isExisting || syncFields.attributes === true;

  if (shouldSyncAttributes) {
    const attributeMappings = sourceFilters.attribute_mappings || [];
    const attributes = mapAttributes(sourceProduct.specifikacije, attributeMappings);

    // Dodaj brand ako postoji
    if (sourceProduct.brand) {
      attributes.unshift({
        name: 'pa_brend',
        options: [sourceProduct.brand],
        visible: true,
        variation: false,
      });
    }

    // Dodaj garanciju ako postoji (formatirano kao "X Mjeseci")
    if (sourceProduct.garancija) {
      const garancija = String(sourceProduct.garancija).trim();
      // Izvuci broj mjeseci iz različitih formata (24, 24M, 2Y, itd.)
      let mjeseci: number | null = null;

      if (/^\d+$/.test(garancija)) {
        // Čisti broj (24, 12, 36...)
        mjeseci = parseInt(garancija, 10);
      } else if (/^\d+M$/i.test(garancija)) {
        // Format XM (24M, 12M...)
        mjeseci = parseInt(garancija, 10);
      } else if (/^\d+Y$/i.test(garancija)) {
        // Format XY (1Y, 2Y, 3Y...) - pretvori godine u mjesece
        mjeseci = parseInt(garancija, 10) * 12;
      }

      if (mjeseci && mjeseci > 0) {
        attributes.push({
          name: 'pa_garancija',
          options: [`${mjeseci} Mjeseci`],
          visible: true,
          variation: false,
        });
      }
    }

    if (attributes.length > 0) {
      data.attributes = attributes;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // META DATA - koristi custom_field_mappings iz shop konfiguracije
  // ─────────────────────────────────────────────────────────────────────────────
  const metaData: { key: string; value: any }[] = [];
  const customFieldMappings = (shop as any).custom_field_mappings || [];

  // Uvijek dodaj osnovne meta podatke
  metaData.push({ key: '_konsolidirana_kolicina', value: consolidatedStock });
  if (sourceProduct.id) {
    metaData.push({ key: '_supabase_id', value: sourceProduct.id });
  }

  // Procesiraj custom field mappings
  for (const mapping of customFieldMappings) {
    if (!mapping.enabled) continue;

    // Provjeri applyTo opciju
    const shouldApply =
      mapping.applyTo === 'all' ||
      (mapping.applyTo === 'new' && !isExisting) ||
      (mapping.applyTo === 'existing' && isExisting);

    if (!shouldApply) continue;

    // Dohvati vrijednost iz source produkta
    const sourceValue = (sourceProduct as any)[mapping.sourceField];

    if (sourceValue !== undefined && sourceValue !== null) {
      let value: any;

      // Specijalno formatiranje za supplier_stocks (uključuje i cijene)
      if (mapping.sourceField === 'supplier_stocks' && typeof sourceValue === 'object') {
        value = formatSupplierStocks(
          sourceValue as Record<string, number>,
          sourceProduct.supplier_prices
        );
      } else if (typeof sourceValue === 'object') {
        // Ostali objekti - stringify
        value = JSON.stringify(sourceValue);
      } else {
        value = sourceValue;
      }

      metaData.push({ key: mapping.targetField, value });
    }
  }

  // Fallback: ako nema custom mappinga, koristi staru logiku
  if (customFieldMappings.length === 0) {
    if (sourceProduct.dobavljac) {
      metaData.push({ key: '_dobavljac', value: sourceProduct.dobavljac });
    }
    if (sourceProduct.nabavna_cijena !== undefined && sourceProduct.nabavna_cijena !== null) {
      metaData.push({ key: '_nabavna_cijena', value: sourceProduct.nabavna_cijena });
    }
    if (sourceProduct.ean) {
      metaData.push({ key: '_ean', value: sourceProduct.ean });
    }
  }

  if (metaData.length > 0) {
    data.meta_data = metaData;
  }

  return {
    data,
    action: isExisting ? 'update' : 'create',
    priceCalculation,
  };
}

// ============================================================================
// SYNC EXECUTION
// ============================================================================

/**
 * Build proxy URL for WooCommerce API (for findTargetProduct)
 */
function buildProxyUrlWithSku(shop: WooShop, endpoint: string, sku: string): string {
  const params = new URLSearchParams({
    url: shop.url,
    endpoint,
    consumer_key: shop.consumer_key,
    consumer_secret: shop.consumer_secret,
    sku,
    per_page: '1',
  });
  return `/api/woo-proxy?${params.toString()}`;
}

/**
 * Pronađi proizvod u target WooCommerce shopu po SKU
 */
export async function findTargetProduct(
  shop: WooShop,
  sku: string
): Promise<WooTargetProduct | null> {
  try {
    const proxyUrl = buildProxyUrlWithSku(shop, 'products', sku);
    const response = await fetch(proxyUrl);

    if (!response.ok) return null;

    const products = await response.json();
    if (products && products.length > 0) {
      return products[0];
    }

    return null;
  } catch (e) {
    console.error('[findTargetProduct] Error:', e);
    return null;
  }
}

/**
 * Helper: Provjeri da li je greška vezana za slike
 */
function isImageError(errorMessage: string): boolean {
  const imageErrorPatterns = [
    'image',
    'slika',
    'greška dobivanja udaljene slike',
    'error getting remote image',
    'invalid image',
    'could not download',
    'not found',
    '404',
    'unable to fetch',
    'failed to download',
    'remote image',
    'upload',
  ];
  const lowerMsg = errorMessage.toLowerCase();
  return imageErrorPatterns.some(pattern => lowerMsg.includes(pattern));
}

/**
 * Build proxy URL for WooCommerce API
 */
function buildProxyUrl(shop: WooShop, endpoint: string): string {
  const params = new URLSearchParams({
    url: shop.url,
    endpoint,
    consumer_key: shop.consumer_key,
    consumer_secret: shop.consumer_secret,
  });
  return `/api/woo-proxy?${params.toString()}`;
}

/**
 * Kreiraj proizvod u WooCommerce
 * Ako kreiranje ne uspije zbog greške slike, automatski pokušava bez slika
 */
export async function createProduct(
  shop: WooShop,
  data: WooProductData
): Promise<{ id: number; sku: string; imagesFailed?: boolean }> {
  const proxyUrl = buildProxyUrl(shop, 'products');

  const makeRequest = async (productData: WooProductData) => {
    // LOG: Šta šaljemo na WooCommerce
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('[CREATE] Šaljem na WooCommerce:');
    console.log('  SKU:', productData.sku);
    console.log('  Name:', productData.name);
    console.log('  Status:', productData.status);
    console.log('  Regular Price:', productData.regular_price);
    console.log('  Sale Price:', productData.sale_price);
    console.log('  Stock Qty:', productData.stock_quantity);
    console.log('  Stock Status:', productData.stock_status);
    console.log('  Categories:', JSON.stringify(productData.categories));
    console.log('  Meta Data:', JSON.stringify(productData.meta_data));
    console.log('  Images:', productData.images?.length || 0, 'slika');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Prvi pokušaj - sa slikama
  try {
    const response = await makeRequest(data);

    // DEBUG: Puni response
    console.log('[CREATE] RAW Response type:', Array.isArray(response) ? 'ARRAY' : 'OBJECT');
    console.log('[CREATE] RAW Response:', JSON.stringify(response).slice(0, 500));

    // WooCommerce može vratiti array ili objekt
    const created = Array.isArray(response) ? response[0] : response;

    console.log('[CREATE] ✅ Kreirano - ID:', created?.id, 'SKU:', created?.sku);

    if (!created || !created.id) {
      throw new Error(`Invalid response from WooCommerce: ${JSON.stringify(response).slice(0, 200)}`);
    }

    return { id: created.id, sku: created.sku };
  } catch (error: any) {
    // Ako je greška vezana za slike i imamo slike, pokušaj bez slika
    if (isImageError(error.message) && data.images && data.images.length > 0) {
      console.log(`[createProduct] Image error for ${data.sku}, retrying without images: ${error.message}`);

      // Ukloni slike i pokušaj ponovo
      const dataWithoutImages = { ...data };
      delete dataWithoutImages.images;

      try {
        const response = await makeRequest(dataWithoutImages);
        const created = Array.isArray(response) ? response[0] : response;
        console.log(`[createProduct] Created ${data.sku} without images (ID: ${created?.id})`);
        return { id: created.id, sku: created.sku, imagesFailed: true };
      } catch (retryError: any) {
        throw new Error(`Retry without images failed: ${retryError.message}`);
      }
    }

    // Nije image error ili nema slika - proslijedi originalnu grešku
    throw error;
  }
}

/**
 * Ažuriraj proizvod u WooCommerce
 */
export async function updateProduct(
  shop: WooShop,
  productId: number,
  data: WooProductData,
  targetProduct?: WooTargetProduct | null
): Promise<{ id: number; sku: string }> {
  const proxyUrl = buildProxyUrl(shop, `products/${productId}`);

  // Za update, ukloni polja koja ne želimo mijenjati
  const updateData = { ...data };

  // NE mijenjaj status za postojeće
  delete updateData.status;

  // SLIKE: Dodaj samo ako target NEMA slike, a source IMA
  const targetHasImages = targetProduct?.images && targetProduct.images.length > 0;
  const sourceHasImages = data.images && data.images.length > 0;

  if (!targetHasImages && sourceHasImages) {
    // Target nema slike, source ima → dodaj slike
    console.log(`[updateProduct] Target nema slike, dodajem ${data.images?.length} slika`);
  } else {
    // Inače ne diraj slike
    delete updateData.images;
  }

  // LOG: Šta šaljemo na WooCommerce
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('[UPDATE] Ažuriram proizvod ID:', productId);
  console.log('  SKU:', updateData.sku);
  console.log('  Name:', updateData.name);
  console.log('  Regular Price:', updateData.regular_price);
  console.log('  Sale Price:', updateData.sale_price);
  console.log('  Stock Qty:', updateData.stock_quantity);
  console.log('  Stock Status:', updateData.stock_status);
  console.log('  Categories:', JSON.stringify(updateData.categories));
  console.log('  Meta Data:', JSON.stringify(updateData.meta_data));
  console.log('  (Status i Images se NE mijenjaju za update)');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  const response = await fetch(proxyUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.log('[UPDATE] ❌ Greška:', error);
    throw new Error(error.message || error.error || `HTTP ${response.status}`);
  }

  const result = await response.json();
  // WooCommerce može vratiti array ili objekt
  const updated = Array.isArray(result) ? result[0] : result;
  console.log('[UPDATE] ✅ Ažurirano - ID:', updated?.id, 'SKU:', updated?.sku);
  return { id: updated.id, sku: updated.sku };
}

/**
 * GLAVNI SYNC - Sinkronizira jedan proizvod
 */
export async function syncSingleProduct(config: SyncConfig): Promise<SyncResult> {
  const { shop, sourceProduct, targetProduct } = config;
  const sku = sourceProduct.sku;

  try {
    // Build product data
    const { data, action, priceCalculation } = buildProductData(config);

    if (action === 'create') {
      // Check sync options
      if (shop.syncProfile?.options?.createNewProducts === false) {
        return {
          action: 'skip',
          success: true,
          sku,
          data,
          message: 'Kreiranje novih proizvoda isključeno',
          priceCalculation,
        };
      }

      const result = await createProduct(shop, data);
      const imageNote = result.imagesFailed ? ' (bez slika - upload nije uspio)' : '';
      return {
        action: 'create',
        success: true,
        productId: result.id,
        sku,
        data,
        message: `Kreiran proizvod ID: ${result.id}${imageNote}`,
        priceCalculation,
      };
    } else {
      // Update
      if (shop.syncProfile?.options?.updateExistingProducts === false) {
        return {
          action: 'skip',
          success: true,
          sku,
          data,
          message: 'Ažuriranje postojećih proizvoda isključeno',
          priceCalculation,
        };
      }

      const result = await updateProduct(shop, targetProduct!.id, data, targetProduct);
      return {
        action: 'update',
        success: true,
        productId: result.id,
        sku,
        data,
        message: `Ažuriran proizvod ID: ${result.id}`,
        priceCalculation,
      };
    }
  } catch (error: any) {
    return {
      action: targetProduct ? 'update' : 'create',
      success: false,
      sku,
      data: {} as WooProductData,
      message: 'Greška',
      error: error.message,
    };
  }
}
